import { useState, type FormEvent, type ReactNode } from 'react';
import { Button, Dialog, Field, Input, Textarea } from '@paige/ui';
import type { ActionState } from './pb-types';
import './partner-billing.css';

export interface ActionDialogProps extends ActionState {
  title: string;
  /** What happens, money and consequences spelled out, before anything is sent. */
  consequence: ReactNode;
  /** The fields the action needs, above the reason. */
  children?: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  /** Ask for a reason (most writes keep one on the audit row). */
  reason?: boolean;
  /** The confirm button waits on more than the reason. */
  ready?: boolean;
  size?: 'sm' | 'md' | 'lg';
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

/**
 * Every partner billing write's dialog: the consequence first, then the fields and the reason, the API's
 * refusal in its own words, and who it is recorded as. Nothing is sent until the confirm button.
 */
export function ActionDialog(props: ActionDialogProps): ReactNode {
  const [reason, setReason] = useState('');
  const needsReason = props.reason !== false;
  const ok = (props.ready ?? true) && (!needsReason || reason.trim() !== '');
  const submit = (e?: FormEvent): void => {
    e?.preventDefault();
    if (ok && !props.busy) props.onConfirm(reason.trim());
  };
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && props.onClose()}
      title={props.title}
      size={props.size ?? 'md'}
      actions={<DialogButtons {...props} ok={ok} onGo={() => submit()} />}
    >
      <form className="pb-dialog" onSubmit={submit} noValidate>
        <div className="pb-consequence">{props.consequence}</div>
        {props.children}
        {needsReason ? <ReasonField value={reason} onChange={setReason} /> : null}
        {props.problem ? (
          <p className="pb-refusal" role="alert">
            {props.problem}
          </p>
        ) : null}
        <p className="pb-actor">
          Recorded as you ({props.actor}), with the time{needsReason ? ' and the reason' : ''}.
        </p>
        <button type="submit" hidden tabIndex={-1} />
      </form>
    </Dialog>
  );
}

function DialogButtons(p: ActionDialogProps & { ok: boolean; onGo: () => void }): ReactNode {
  return (
    <>
      <Button onClick={p.onClose}>Cancel</Button>
      <Button
        variant={p.danger ? 'danger' : 'primary'}
        disabled={!p.ok}
        loading={p.busy}
        onClick={p.onGo}
      >
        {p.confirmLabel}
      </Button>
    </>
  );
}

function ReasonField(p: { value: string; onChange: (v: string) => void }): ReactNode {
  return (
    <Field label="Reason" hint="Kept on the audit row. 1 to 500 characters." required>
      {({ id, describedBy }) => (
        <Textarea
          id={id}
          aria-describedby={describedBy}
          value={p.value}
          maxLength={500}
          rows={2}
          onChange={(e) => p.onChange(e.target.value)}
        />
      )}
    </Field>
  );
}

/** A labelled text, number or date input for the dialogs. */
export function TextInput(p: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'number' | 'date';
  hint?: string;
  max?: number;
  step?: string;
  required?: boolean;
  inputMode?: 'decimal' | 'numeric';
}): ReactNode {
  return (
    <Field label={p.label} hint={p.hint} required={p.required}>
      {({ id, describedBy }) => (
        <Input
          id={id}
          aria-describedby={describedBy}
          type={p.type ?? 'text'}
          value={p.value}
          maxLength={p.max}
          step={p.step}
          inputMode={p.inputMode}
          onChange={(e) => p.onChange(e.target.value)}
        />
      )}
    </Field>
  );
}
